/**
 * Page: CustomerLists
 * Admin-only page showing categorized customer email lists with CSV export.
 * Fetches data from the backend, provides refresh, error handling, and tables.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../../components/shared/Footer';
import { ArrowLeft, Users, Download, RefreshCw, Calendar, Mail, Clock, RotateCcw, Crown } from 'lucide-react';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import api from '../../../api';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function CustomerLists() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [customerData, setCustomerData] = useState({
    payingCustomers: { count: 0, users: [] },
    trialUsers: { count: 0, users: [] },
    formerSubscribers: { count: 0, users: [] },
    basicUsers: { count: 0, users: [] },
    summary: { 
      totalUsers: 0, 
      totalPaying: 0, 
      totalTrial: 0, 
      totalFormerSubscribers: 0, 
      totalBasic: 0, 
      timestamp: null 
    }
  });
  
  // Get the auth context
  const { user } = useAuth();
  const { addToast } = useToast();
  
  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  // Delay showing errors to prevent flashing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setShowError(true);
      }, 500); 
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error]);

  // Load customer data
  const loadCustomerData = async (showRefreshing = false) => {
    if (showRefreshing) {
      setIsRefreshing(true);
    }
    setError(null);
    
    try {
      const response = await api.get('/admin/users/customer-lists');
      setCustomerData(response);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to load customer lists data');
      addToast('error', 'Failed to load customer lists data');
    } finally {
      setIsLoading(false);
      if (showRefreshing) {
        setIsRefreshing(false);
      }
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (isAdmin) {
      loadCustomerData();
    }
  }, [isAdmin]);

  const handleRefresh = () => {
    loadCustomerData(true);
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never';
    
    const now = new Date();
    const activeDate = lastActive instanceof Date ? lastActive : new Date(lastActive);
    const diffTime = Math.abs(now - activeDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    if (diffDays <= 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays <= 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  // CSV download functionality
  const downloadCSV = (users, filename) => {
    if (!users || users.length === 0) {
      addToast('warning', 'No data to export');
      return;
    }

    // Determine if this includes additional columns (paying customers and trial users get all columns)
    const includeExtraColumns = filename.includes('paying') || filename.includes('trial');
    
    // Create CSV content with conditional headers
    const headers = includeExtraColumns 
      ? ['Email', 'First Name', 'Last Name', 'Plan Level', 'Subscription Status', 'Created Date', 'Last Active']
      : ['Email', 'First Name', 'Last Name', 'Created Date'];
      
    const csvContent = [
      headers.join(','),
      ...users.map(user => {
        const baseData = [
          `"${user.email || ''}"`,
          `"${user.firstName || ''}"`,
          `"${user.lastName || ''}"`,
        ];
        
        if (includeExtraColumns) {
          return [
            ...baseData,
            `"${user.planLevel || 'basic'}"`,
            `"${user.subscriptionStatus || 'inactive'}"`,
            `"${user.createdAt ? formatDate(user.createdAt) : 'Unknown'}"`,
            `"${user.lastActive ? formatDate(user.lastActive) : 'Never'}"`
          ].join(',');
        } else {
          return [
            ...baseData,
            `"${user.createdAt ? formatDate(user.createdAt) : 'Unknown'}"`
          ].join(',');
        }
      })
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    addToast('success', `CSV file downloaded: ${filename}.csv`);
  };

  // Get icon and color for each user type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'paying':
        return <Crown className="h-5 w-5 text-green-500 mr-2" />;
      case 'trial':
        return <Clock className="h-5 w-5 text-blue-500 mr-2" />;
      case 'former':
        return <RotateCcw className="h-5 w-5 text-orange-500 mr-2" />;
      case 'basic':
        return <Users className="h-5 w-5 text-gray-500 mr-2" />;
      default:
        return <Users className="h-5 w-5 text-gray-500 mr-2" />;
    }
  };

  // User table component
  const UserTable = ({ users, title, type }) => (
    <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getTypeIcon(type)}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                {title}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {users.length} {users.length === 1 ? 'user' : 'users'}
              </p>
            </div>
          </div>
          <button
            onClick={() => downloadCSV(users, title.toLowerCase().replace(/\s+/g, '_'))}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        {users && users.length > 0 ? (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Mail className="w-3 h-3" />
                    <span>Email</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <span>First Name</span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <span>Last Name</span>
                </th>
                {(type === 'paying' || type === 'trial') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <span>Plan Level</span>
                  </th>
                )}
                {(type === 'paying' || type === 'trial') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <span>Status</span>
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-3 h-3" />
                    <span>Created</span>
                  </div>
                </th>
                {(type === 'paying' || type === 'trial') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <span>Last Active</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user, index) => (
                <tr key={user.email || index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {user.email || 'No email'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.firstName || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.lastName || '-'}
                  </td>
                  {(type === 'paying' || type === 'trial') && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.planLevel === 'premium' 
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                      }`}>
                        {user.planLevel || 'basic'}
                      </span>
                    </td>
                  )}
                  {(type === 'paying' || type === 'trial') && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.subscriptionStatus === 'active' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                          : user.subscriptionStatus === 'trialing'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                          : user.subscriptionStatus === 'past_due'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                      }`}>
                        {user.subscriptionStatus || 'inactive'}
                      </span>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(user.createdAt)}
                  </td>
                  {(type === 'paying' || type === 'trial') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatLastActive(user.lastActive)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No users found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No users match this category at the moment.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading customer lists..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  return (
    <div className="p-6 w-full max-w-[1800px] mx-auto pb-20">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/users')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to User Management
        </button>
      </div>

      {error && showError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Users className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Beehiiv Newsletter Emails</h1>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Categorized email lists for paying users, trial users, former subscribers, and basic users with CSV export functionality for newsletter management.
        </p>
      </div>

      {/* Summary Stats */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Crown className="h-5 w-5 text-green-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Paying Customers</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{customerData.summary?.totalPaying || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-blue-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Trial Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{customerData.summary?.totalTrial || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <RotateCcw className="h-5 w-5 text-orange-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Former Subscribers</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{customerData.summary?.totalFormerSubscribers || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Users className="h-5 w-5 text-gray-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Basic Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{customerData.summary?.totalBasic || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Lists */}
      <div className="space-y-8">
        {/* Paying Customers */}
        <UserTable 
          users={customerData.payingCustomers?.users || []}
          title="Paying Customers"
          type="paying"
        />

        {/* Trial Users */}
        <UserTable 
          users={customerData.trialUsers?.users || []}
          title="Trial Users"
          type="trial"
        />

        {/* Former Subscribers */}
        <UserTable 
          users={customerData.formerSubscribers?.users || []}
          title="Former Subscribers"
          type="former"
        />

        {/* Basic Users */}
        <UserTable 
          users={customerData.basicUsers?.users || []}
          title="Basic Users"
          type="basic"
        />
      </div>

      <Footer />
    </div>
  );
} 